import { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import lodashSet from 'lodash/set';
import lodashGet from 'lodash/get';
import { ZodError, ZodIssue, ZodType } from 'zod';

export type ZodTranslator = (errorObj: ZodIssue) => string | ReactNode;

export interface ZodValidationError {
  [key: string]: ZodError | string[];
}

function normalizeZodValidationError(
  err: ZodError,
  translator?: ZodTranslator,
): ZodValidationError {
  return err.issues.reduce((errors, innerError) => {
    const { path, message } = innerError;
    const extendedPath = path
      .map((item) => (typeof item === 'number' ? `[${item}]` : item))
      .join('.');
    const el: ReturnType<ZodTranslator> = translator ? translator(innerError) : message;

    if (path === undefined) {
      return errors;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (errors.hasOwnProperty(extendedPath)) {
      const prev = lodashGet(errors, path);
      prev.push(el);
      lodashSet(errors, extendedPath, prev);
    } else {
      lodashSet(errors, extendedPath, [el]);
    }
    return errors;
  }, {});
}
function logError(err: unknown) {
  console.error(err);
  Sentry.captureException(err);
}

const UNEXPECTED_ERROR_MESSAGE = 'Unexpected validation error';

export function makeZodValidate<T>(validator: ZodType<T>, translator?: ZodTranslator) {
  return async (values: T): Promise<ZodValidationError> => {
    try {
      await validator.parseAsync(values);
      return {};
    } catch (err) {
      if (err instanceof ZodError) {
        return normalizeZodValidationError(err, translator);
      }
      // Need log the error otherwise the final-form will intercept it and say nothing
      logError(err);
      return { _global: [UNEXPECTED_ERROR_MESSAGE] };
    }
  };
}
